import { memo } from 'react';
import { TableRow, TableCell } from '@mui/material';
import DataTableCell from './DataTableCell';
import clsx from 'clsx';
import styles from './styles.module.css';

function DataTableRow(props: any) {
    const {
        item,
        headers,
        onSelectRow,
        selected,
        selectedItemFromAction,
        columnWidths = [],
        getRowDisabled,
        legendColor
    } = props;

    const isDisabledRow = getRowDisabled?.(item);
    /*
     * Grupo de filas (`rowSpan`): la primera abarca (n > 1) y las siguientes son
     * sub-filas (0). El separador entre filas es el `outline` del `tr`, que
     * dibujaría una línea entre la primera y sus sub-filas, cruzando la celda
     * abarcada. En el grupo se saca el outline y el grupo se lee como una fila
     * con sub-filas separadas por espacio; la primera fila lleva borde superior
     * para que dos grupos seguidos no se peguen.
     */
    const spans = headers.map((header: any) => header?.rowSpan?.(item));
    const isGroupStart = spans.some((span: any) => span > 1);
    const isContinuationRow = spans.some((span: any) => span === 0);
    return (
        <TableRow
            className={`${styles['datatable-rows']} ${isDisabledRow ? styles['row-disabled'] : ''}`}
            selected={
                item?.id !== undefined && selected?.id !== undefined
                    ? item.id === selected.id
                    : false
            }
            sx={{
                borderLeft: legendColor ? `4px solid ${legendColor}` : undefined,
                ...((isGroupStart || isContinuationRow) && {
                    // `!important`: pisa el `outline` de `.datatable-rows` del css module.
                    outline: 'none !important'
                }),
                ...(isGroupStart && { '& > td': { borderTop: '0.5px solid #e0ebed' } })
            }}
        >
            {headers.map((header: any, subIndex: number) => {
                /*
                 * `rowSpan` opcional por columna: `(item) => n`. Con n > 1 la
                 * celda abarca las n filas siguientes; con 0 no se dibuja porque
                 * ya la abarcó una fila anterior. Sirve para agrupar filas que
                 * comparten la primera columna (SVCC: un CUIT con varios
                 * períodos). Sin la función, todo sigue igual.
                 */
                const span = header?.rowSpan?.(item);
                if (span === 0) return null;
                const isFirstColumn = subIndex === 0;
                const isLastColumn = subIndex === headers.length - 1;
                const isStickyAction = props.isMobile && header?.type === 'action' && isLastColumn;
                const hasMaxWidth = header?.maxWidth !== undefined && header?.maxWidth !== '';

                let className = '';
                if (!hasMaxWidth) {
                    className = styles['datatable-tr-td-overflow'];
                } else {
                    className = styles['cell-with-max-width'];
                }

                return (
                    <TableCell
                        key={subIndex}
                        rowSpan={span && span > 1 ? span : undefined}
                        className={clsx(styles.cellBase, styles.wrapCell)}
                        align={header?.type === 'action' ? header?.align || 'center' : header?.align || 'left'}
                        onClick={(e) =>
                            ['action', 'selection', 'presentation-options'].includes(header?.type)
                                ? header.onClick?.(e, item)
                                : onSelectRow?.(item)
                        }
                        sx={{
                            width: header?.width || columnWidths[subIndex],
                            maxWidth: columnWidths[subIndex],
                            position: isStickyAction ? 'sticky' : 'static',
                            right: isStickyAction ? 0 : undefined,
                            backgroundColor: isStickyAction ? '#fcfcfc' : undefined,
                            minWidth: 0,

                            ...(isFirstColumn &&
                                legendColor && {
                                    borderLeft: `4px solid ${legendColor}`,
                                    paddingLeft: '20px !important'
                                })
                        }}
                    >
                        <DataTableCell
                            onSelectRow={onSelectRow}
                            header={header}
                            item={item}
                            index={subIndex}
                            selectedItemFromAction={selectedItemFromAction}
                            {...props}
                        />
                    </TableCell>
                );
            })}
        </TableRow>
    );
}

export default memo(DataTableRow);
